'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';

import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import ProductTypeBadge from '@/app/(football)/[team]/store/_components/ProductTypeBadge';
import { trackEvent } from '@/libs/analytics/events';
import { momentTimezone } from '@/utils/time';

export interface ListItemProps {
  productId: string;
  name: string;
  startDateTime: number;
  endDateTime: number;
  badge?: string;
  image: string;
  originalPrice: number;
  discountType: string;
  discountValue: number;
  price: number;
  stock: number;
  isSoldOut: boolean;
}

export default function ListItem({
  productId,
  name,
  startDateTime,
  endDateTime,
  badge,
  image,
  originalPrice,
  discountType,
  discountValue,
  price,
  stock,
  isSoldOut,
}: ListItemProps) {
  const { teamSlug } = useTeamConfig();

  // Calculate discount rate based on discountType
  const discountRate =
    discountType === 'P'
      ? discountValue // Percentage discount
      : originalPrice > 0
        ? Math.round(((originalPrice - price) / originalPrice) * 100) // Calculate percentage from price difference
        : 0;

  const hasDiscount = discountRate > 0;

  // Check if product is new (within 2 weeks from start date)
  const isNewProduct = useMemo(() => {
    const startDate = momentTimezone(startDateTime);
    const currentDate = momentTimezone();
    const daysDifference = currentDate.diff(startDate, 'days');

    // Product is new if it started between 0 and 14 days ago
    return daysDifference >= 0 && daysDifference <= 14;
  }, [startDateTime]);

  const handleSelectItem = () => {
    trackEvent({
      name: 'select_item',
      params: {
        items: [{
          item_id: productId,
          item_name: name,
          item_brand: teamSlug,
          item_category: 'store',
          price,
        }],
        list_name: 'store',
        migration_source: 'gtm',
      },
    });
  };

  return (
    <Link href={`/${teamSlug}/store/${productId}`} onClick={handleSelectItem}>
      <li className="flex w-full flex-col items-center gap-4">
        {/* Image Container */}
        <div className="relative flex aspect-square w-full overflow-hidden rounded bg-store-list-image-bg gradient-border gradient-border-store-list">
          <Image src={image} alt={name} fill className="object-cover" />

          {/* Badge Container */}
          <div className="absolute top-2 left-2 flex items-center gap-0.5">
            {isNewProduct && <ProductTypeBadge badge="NEW" />}
            {badge && <ProductTypeBadge badge={badge} />}
          </div>

          {/* Sold Out Overlay */}
          {isSoldOut && (
            <span className="absolute inset-0 flex items-center justify-center bg-store-list-soldout-overlay text-base leading-5 font-extrabold text-store-list-soldout-text">
              SOLD OUT
            </span>
          )}
        </div>

        {/* Info Container */}
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full flex-col items-start gap-1">
            {/* Date */}
            <span className="line-clamp-1 overflow-hidden text-xs leading-[14px] font-medium text-ellipsis text-store-list-text-primary">
              {momentTimezone(startDateTime).format('YY.MM.DD HH:mm')} ~{' '}
              {momentTimezone(endDateTime).format('YY.MM.DD HH:mm')}
            </span>
            {/* Name */}
            <p className="line-clamp-2 w-full overflow-hidden text-base leading-5 font-bold text-ellipsis text-store-list-text-primary">
              {name}
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-1">
            <div className="flex w-full flex-col items-start justify-center gap-0.5">
              {/* Original Price */}
              {hasDiscount && (
                <span className="line-clamp-1 overflow-hidden text-sm leading-[18px] font-normal text-ellipsis text-store-list-text-primary line-through">
                  {originalPrice.toLocaleString('ko-KR')}원
                </span>
              )}
              {/* Discount Info */}
              <div className="flex flex-wrap items-center gap-0.5">
                {hasDiscount && (
                  <span className="line-clamp-1 overflow-hidden text-sm leading-[18px] font-bold text-ellipsis text-store-list-text-discount">
                    {discountRate}%
                  </span>
                )}
                <span className="line-clamp-1 overflow-hidden text-base leading-5 font-extrabold text-ellipsis text-store-list-text-primary">
                  {price.toLocaleString('ko-KR')}원
                </span>
              </div>
            </div>
          </div>

          {/* Remaining Stock */}
          {stock < 10000 && (
            <span className="line-clamp-1 overflow-hidden text-xs leading-[14px] font-medium text-ellipsis text-store-list-text-stock">
              (잔여수량 : {stock?.toLocaleString('ko-KR')}개)
            </span>
          )}
        </div>
      </li>
    </Link>
  );
}
